// Multi-phone editor used wherever a "subject" (client, contractor)
// has phone numbers. Each row holds a phone type (looked up from
// lookup_options) plus a formatted phone string. The first row is
// implicitly primary; the API marks phones[0].is_primary on save.
//
// The local state holds the formatted display form ("(555) 123-4567")
// for the input field; submit handlers normalize to the 10-digit
// storage form via stripPhoneForStorage from utils/phone.

import type { LookupOption } from '@stfors/shared-types';
import { PhoneInput } from './PhoneInput';
import './PhoneListInput.css';

export interface PhoneEntry {
  phone_type: string;
  phone: string;       // formatted (display) form during editing
}

export interface PhoneListInputProps {
  value: PhoneEntry[];
  onChange: (next: PhoneEntry[]) => void;
  typeOptions: LookupOption[];
  disabled?: boolean;
  /** Default phone_type for newly added rows. Falls back to the first
   *  active option in typeOptions. */
  defaultType?: string;
}

export function PhoneListInput(props: PhoneListInputProps): JSX.Element {
  const { value, onChange, typeOptions, disabled = false, defaultType } = props;

  const nextDefaultType = (): string => {
    if (defaultType && typeOptions.some((o) => o.value === defaultType)) {
      return defaultType;
    }
    return typeOptions[0]?.value ?? 'Mobile';
  };

  const setRow = (idx: number, patch: Partial<PhoneEntry>): void => {
    const next = value.map((row, i) => (i === idx ? { ...row, ...patch } : row));
    onChange(next);
  };

  const removeRow = (idx: number): void => {
    onChange(value.filter((_, i) => i !== idx));
  };

  // Primary = the first row (the API flags phones[0].is_primary on save), so
  // "make primary" just moves the chosen row to the top.
  const makePrimary = (idx: number): void => {
    if (idx === 0) return;
    const next = [...value];
    const [row] = next.splice(idx, 1);
    next.unshift(row!);
    onChange(next);
  };

  const addRow = (): void => {
    onChange([...value, { phone_type: nextDefaultType(), phone: '' }]);
  };

  return (
    <div className="phone-list">
      {value.length === 0 && (
        <p className="phone-list-empty">No phone numbers yet.</p>
      )}
      {value.map((row, idx) => (
        <div key={idx} className="phone-list-row">
          <select
            className="form-input phone-list-type"
            value={row.phone_type}
            onChange={(e) => setRow(idx, { phone_type: e.target.value })}
            disabled={disabled}
            aria-label={`Phone ${idx + 1} type`}
          >
            {/* Keep the current value visible even if it's been deleted
                from the lookup table — admins can correct it. */}
            {!typeOptions.some((o) => o.value === row.phone_type) && row.phone_type && (
              <option value={row.phone_type}>{row.phone_type} (legacy)</option>
            )}
            {typeOptions.map((o) => (
              <option key={o.id} value={o.value}>{o.value}</option>
            ))}
          </select>
          <PhoneInput
            className="phone-list-input"
            value={row.phone}
            onChange={(next) => setRow(idx, { phone: next })}
            disabled={disabled}
            aria-label={`Phone ${idx + 1} number`}
          />
          {value.length > 1 ? (
            idx === 0 ? (
              <span className="phone-list-primary-badge" title="Primary phone number">Primary</span>
            ) : (
              <button
                type="button"
                className="phone-list-make-primary"
                onClick={() => makePrimary(idx)}
                disabled={disabled}
                title="Make this the primary phone number"
              >
                Make primary
              </button>
            )
          ) : (
            <span className="phone-list-primary-slot" aria-hidden="true" />
          )}
          <button
            type="button"
            className="phone-list-remove"
            onClick={() => removeRow(idx)}
            disabled={disabled}
            aria-label={`Remove phone ${idx + 1}`}
            title={`Remove phone ${idx + 1}`}
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        className="phone-list-add"
        onClick={addRow}
        disabled={disabled}
      >
        + Add phone
      </button>
    </div>
  );
}
